import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import API from '../services/api';
import { logout } from '../services/authService';
import './Profile.css';

function Profile({ user, posts = [], savedPosts: savedPostsProp = [], onAvatarUpdate }) {
    const navigate = useNavigate();
    const [activeTab, setActiveTab] = useState('paylasimlar');

    const userPosts = posts.filter(p => p.author_id === user?.id);
    const savedPosts = savedPostsProp;

    const [userComments, setUserComments] = useState([]);
    const [likedPosts, setLikedPosts] = useState([]);
    const [commentsLoading, setCommentsLoading] = useState(true);
    const [likedLoading, setLikedLoading] = useState(true);

    const fetchComments = useCallback(() => {
        if (!user) return;
        setCommentsLoading(true);
        API.get('/profiles/me/comments')
            .then(res => setUserComments(res.data || []))
            .catch(() => setUserComments([]))
            .finally(() => setCommentsLoading(false));
    }, [user]);

    const fetchLikedPosts = useCallback(() => {
        if (!user) return;
        setLikedLoading(true);
        API.get('/votes/mine?target_type=post')
            .then(res => setLikedPosts(res.data || []))
            .catch(() => setLikedPosts([]))
            .finally(() => setLikedLoading(false));
    }, [user]);

    // Sohbet konularım — backend'in Home listesinde Sohbet kategorisini
    // otomatik hariç tutması yüzünden "Paylaşımlar" sekmesinde hiç görünmüyorlardı.
    // Yeni bir backend ucu gerekmiyor: GET /posts?category=sohbet zaten var,
    // yazara göre burada (client-side) filtreliyoruz.
    const [sohbetTopics, setSohbetTopics] = useState([]);
    const [sohbetLoading, setSohbetLoading] = useState(true);

    const fetchSohbetTopics = useCallback(() => {
        if (!user) return;
        setSohbetLoading(true);
        API.get('/posts?category=sohbet')
            .then(res => setSohbetTopics((res.data || []).filter(t => t.author_id === user.id)))
            .catch(() => setSohbetTopics([]))
            .finally(() => setSohbetLoading(false));
    }, [user]);

    useEffect(() => {
        fetchComments();
        fetchLikedPosts();
        fetchSohbetTopics();
    }, [fetchComments, fetchLikedPosts, fetchSohbetTopics]);

    const fileInputRef = useRef(null);
    const [avatarUploading, setAvatarUploading] = useState(false);

    const handleAvatarClick = () => {
        fileInputRef.current?.click();
    };

    const handleAvatarChange = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;

        if (!file.type.startsWith('image/')) {
            alert('Lütfen bir resim dosyası seç.');
            return;
        }

        try {
            setAvatarUploading(true);
            const formData = new FormData();
            formData.append('photo', file);
            const res = await API.upload('/profiles/avatar', formData);
            const newAvatarUrl = res.data?.avatar_url || res.data?.url;

            if (newAvatarUrl && onAvatarUpdate) {
                onAvatarUpdate(newAvatarUrl);
            }
        } catch (err) {
            alert('Avatar yüklenirken hata oluştu: ' + err.message);
        } finally {
            setAvatarUploading(false);
            e.target.value = '';
        }
    };

    const handleLogout = () => {
        logout();
        localStorage.removeItem("user");
        window.location.href = "/login";
    };

    if (!user) {
        return (
            <div className="profile-container">
                <div style={{ textAlign: 'center', padding: '40px 20px' }}>
                    <h2>Profilini görmek için giriş yapmalısın.</h2>
                    <button className="login-submit-btn" style={{ marginTop: '16px' }} onClick={() => navigate('/login')}>
                        Giriş Yap
                    </button>
                </div>
            </div>
        );
    }

    const username = user.profiles?.username || user.username || 'Kullanıcı';
    const avatarUrl = user.profiles?.avatar_url || user.avatar || "https://ui-avatars.com/api/?name=" + encodeURIComponent(username) + "&background=random";

    const renderContent = () => {
        switch (activeTab) {
            case 'paylasimlar':
                return userPosts.length > 0 ? (
                    <div className="profile-post-list">
                        {userPosts.map(post => (
                            <div
                                key={post.id}
                                className="profile-list-item"
                                onClick={() => navigate(`/post/${post.id}`)}
                                style={{ cursor: 'pointer', padding: '12px 0', borderBottom: '1px solid var(--line)' }}
                            >
                                <strong>{post.title}</strong>
                                <div style={{ fontSize: '12px', color: 'var(--muted)', marginTop: '4px' }}>
                                    {post.view_count ?? post.views ?? 0} Görüntülenme • {((post.upvote_count || 0) - (post.downvote_count || 0)) || post.votes || 0} Beğeni
                                </div>
                            </div>
                        ))}
                    </div>
                ) : (
                    <div>Henüz bir paylaşım yapmadın.</div>
                );

            case 'yorumlar':
                if (commentsLoading) return <div>Yorumların yükleniyor...</div>;
                return userComments.length > 0 ? (
                    <div className="profile-comment-list">
                        {userComments.map(comment => (
                            <div
                                key={comment.id}
                                className="profile-list-item"
                                onClick={() => navigate(`/post/${comment.topic_id || comment.post_id}#comment-${comment.id}`)}
                                style={{ cursor: 'pointer', padding: '12px 0', borderBottom: '1px solid var(--line)' }}
                            >
                                <p style={{ margin: 0, fontSize: '14px', color: 'var(--ink)' }}>"{comment.body || comment.content || comment.text}"</p>
                                <div style={{ fontSize: '12px', color: 'var(--muted)', marginTop: '4px' }}>
                                    {comment.post_title ? `"${comment.post_title}" gönderisine yazıldı` : 'Bir gönderiye yazıldı'} • {new Date(comment.created_at).toLocaleDateString('tr-TR')}
                                </div>
                            </div>
                        ))}
                    </div>
                ) : (
                    <div>Henüz hiç yorum yapmadın.</div>
                );

            case 'kaydedilenler':
                return savedPosts.length > 0 ? (
                    <div className="profile-saved-list">
                        {savedPosts.map(post => (
                            <div
                                key={post.id}
                                className="profile-list-item"
                                onClick={() => navigate(`/post/${post.id}`)}
                                style={{ cursor: 'pointer', padding: '12px 0', borderBottom: '1px solid var(--line)' }}
                            >
                                <strong>{post.title}</strong>
                            </div>
                        ))}
                    </div>
                ) : (
                    <div>Kaydedilen içerik bulunamadı.</div>
                );

            case 'beğenilenler':
                if (likedLoading) return <div>Beğenilerin yükleniyor...</div>;
                return likedPosts.length > 0 ? (
                    <div className="profile-liked-list">
                        {likedPosts.map(post => (
                            <div
                                key={post.id}
                                className="profile-list-item"
                                onClick={() => navigate(`/post/${post.id}`)}
                                style={{ cursor: 'pointer', padding: '12px 0', borderBottom: '1px solid var(--line)' }}
                            >
                                <strong>{post.title}</strong>
                            </div>
                        ))}
                    </div>
                ) : (
                    <div>Beğendiğin gönderiler burada görünecek.</div>
                );

            case 'sohbet-konularim':
                if (sohbetLoading) return <div>Sohbet konuların yükleniyor...</div>;
                return sohbetTopics.length > 0 ? (
                    <div className="profile-sohbet-list">
                        {sohbetTopics.map(topic => (
                            <div
                                key={topic.id}
                                className="profile-list-item"
                                onClick={() => navigate(`/sohbet/${topic.id}`)}
                                style={{ cursor: 'pointer', padding: '12px 0', borderBottom: '1px solid var(--line)' }}
                            >
                                <strong>💬 {topic.title}</strong>
                                <div style={{ fontSize: '12px', color: 'var(--muted)', marginTop: '4px' }}>
                                    {topic.comment_count ?? 0} yanıt • {new Date(topic.created_at).toLocaleDateString('tr-TR')}
                                </div>
                            </div>
                        ))}
                    </div>
                ) : (
                    <div>Henüz bir sohbet konusu açmadın.</div>
                );

            default:
                return <div>İçerik bulunamadı.</div>;
        }
    };

    return (
        <div className="profile-container">
            <div className="profile-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '6px' }}>
                        <div className="profile-avatar" onClick={handleAvatarClick} style={{ cursor: 'pointer', position: 'relative' }} title="Avatarı değiştirmek için tıkla">
                            <img 
                                src={avatarUrl} 
                                alt="User Avatar" 
                                style={{ width: '80px', height: '80px', borderRadius: '50%', objectFit: 'cover', opacity: avatarUploading ? 0.5 : 1 }}
                            />
                            <div style={{
                                position: 'absolute', bottom: 0, right: 0, background: 'var(--accent)', color: '#fff',
                                borderRadius: '50%', width: '24px', height: '24px', display: 'flex',
                                alignItems: 'center', justifyContent: 'center', fontSize: '12px', border: '2px solid #fff'
                            }}>
                                {avatarUploading ? '…' : '📷'}
                            </div>
                            <input
                                type="file"
                                accept="image/*"
                                ref={fileInputRef}
                                onChange={handleAvatarChange}
                                style={{ display: 'none' }}
                            />
                        </div>
                        <span
                            onClick={handleAvatarClick}
                            style={{ fontSize: '12px', color: 'var(--accent)', cursor: 'pointer', fontWeight: 600 }}
                        >
                            {avatarUploading ? 'Yükleniyor...' : 'Profil fotoğrafını düzenle'}
                        </span>
                    </div>
                    <div className="profile-info">
                        <h2>@{username}</h2>
                        <p style={{ color: 'var(--muted)', margin: '2px 0' }}>{user.email}</p>
                        {user.role && <span className="user-role-badge" style={{ fontSize: '12px', background: 'var(--line)', padding: '2px 8px', borderRadius: '4px' }}>{user.role}</span>}
                    </div>
                </div>

                <button 
                    onClick={handleLogout} 
                    style={{ padding: '8px 16px', background: 'var(--danger)', color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer', fontWeight: 'bold' }}
                >
                    Çıkış Yap
                </button>
            </div>

            <div className="profile-tabs" style={{ marginTop: '20px', borderBottom: '2px solid var(--line)' }}>
                <button
                    className={activeTab === 'paylasimlar' ? 'tab-btn active' : 'tab-btn'}
                    onClick={() => setActiveTab('paylasimlar')}
                >
                    Paylaşımlar ({userPosts.length})
                </button>
                <button
                    className={activeTab === 'yorumlar' ? 'tab-btn active' : 'tab-btn'}
                    onClick={() => setActiveTab('yorumlar')}
                >
                    Yorumlar ({userComments.length})
                </button>
                <button
                    className={activeTab === 'kaydedilenler' ? 'tab-btn active' : 'tab-btn'}
                    onClick={() => setActiveTab('kaydedilenler')}
                >
                    Kaydedildi ({savedPosts.length})
                </button>
                <button
                    className={activeTab === 'beğenilenler' ? 'tab-btn active' : 'tab-btn'}
                    onClick={() => setActiveTab('beğenilenler')}
                >
                    Beğenilenler ({likedPosts.length})
                </button>
                <button
                    className={activeTab === 'sohbet-konularim' ? 'tab-btn active' : 'tab-btn'}
                    onClick={() => setActiveTab('sohbet-konularim')}
                >
                    💬 Sohbet Konularım ({sohbetTopics.length})
                </button>
            </div>

            <div className="tab-content" style={{ paddingTop: '15px' }}>
                {renderContent()}
            </div>
        </div>
    );
}

export default Profile;